/**
 * TeamPage - Dedicated page for viewing team details.
 *
 * Route: /admin/teams/:teamId
 */

import { useState, useMemo } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Button,
  Breadcrumbs,
  Link,
  IconButton,
  Tooltip,
  TextField,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import PersonIcon from '@mui/icons-material/Person';
import ReceiptIcon from '@mui/icons-material/Receipt';
import BackIcon from '@mui/icons-material/ArrowBack';
import CopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DisqualifyIcon from '@mui/icons-material/Block';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { teamsApi, gamesApi, teamCommentsApi } from '@/api/client';
import { EventLog } from '@/components/EventLog';
import { GamesListTable } from '@/components/GamesListTable';
import { getGameStatus } from '@/types';

export function TeamPage() {
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const teamIdNum = teamId ? parseInt(teamId, 10) : null;

  // Copy feedback
  const [copied, setCopied] = useState(false);

  // Comment input state
  const [newComment, setNewComment] = useState('');

  // Disqualify dialog state
  const [disqualifyDialogOpen, setDisqualifyDialogOpen] = useState(false);

  // Undisqualify dialog state
  const [undisqualifyDialogOpen, setUndisqualifyDialogOpen] = useState(false);

  const handleGameClick = (gameId: number) => {
    navigate(`/admin/games/${gameId}`);
  };

  const handleBlockClick = (paperId: number) => {
    navigate(`/admin/blocks/${paperId}`);
  };

  const handleCourtClick = (courtId: number) => {
    navigate(`/admin/courts/${courtId}`);
  };

  const handleTeamClick = (teamId: number) => {
    navigate(`/admin/teams/${teamId}`);
  };

  const handleCopyPhone = async (phone: string) => {
    try {
      await navigator.clipboard.writeText(phone);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy phone:', err);
    }
  };

  // Fetch team details
  const {
    data: team,
    isLoading: isLoadingTeam,
    error: teamError,
  } = useQuery({
    queryKey: ['team', teamIdNum],
    queryFn: () => teamsApi.get(teamIdNum!),
    enabled: teamIdNum !== null,
  });

  // Fetch games for this team using server-side filtering
  const { data: teamGames = [], isLoading: isLoadingGames } = useQuery({
    queryKey: ['games', 'team', teamIdNum],
    queryFn: () => gamesApi.list({ team: teamIdNum! }),
    enabled: teamIdNum !== null,
  });

  // Fetch comments for this team
  const { data: comments = [] } = useQuery({
    queryKey: ['team-comments', teamIdNum],
    queryFn: () => teamCommentsApi.list(teamIdNum!),
    enabled: teamIdNum !== null,
  });

  // Create comment mutation
  const createCommentMutation = useMutation({
    mutationFn: (comment: string) => teamCommentsApi.create(teamIdNum!, comment),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-comments', teamIdNum] });
      setNewComment('');
    },
  });

  // Delete comment mutation
  const deleteCommentMutation = useMutation({
    mutationFn: (commentId: number) => teamCommentsApi.delete(teamIdNum!, commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-comments', teamIdNum] });
    },
  });

  // Disqualify mutation
  const disqualifyMutation = useMutation({
    mutationFn: (id: number) => teamsApi.disqualify(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team', teamIdNum] });
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      setDisqualifyDialogOpen(false);
    },
  });

  // Undisqualify mutation
  const undisqualifyMutation = useMutation({
    mutationFn: (id: number) => teamsApi.undisqualify(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team', teamIdNum] });
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      queryClient.invalidateQueries({ queryKey: ['games'] });
      setUndisqualifyDialogOpen(false);
    },
  });

  const handleDisqualifyClick = () => {
    setDisqualifyDialogOpen(true);
  };

  const handleDisqualifyConfirm = () => {
    if (teamIdNum) {
      disqualifyMutation.mutate(teamIdNum);
    }
  };

  const handleUndisqualifyClick = () => {
    setUndisqualifyDialogOpen(true);
  };

  const handleUndisqualifyConfirm = () => {
    if (teamIdNum) {
      undisqualifyMutation.mutate(teamIdNum);
    }
  };

  const handleAddComment = () => {
    if (newComment.trim()) {
      createCommentMutation.mutate(newComment.trim());
    }
  };

  // Calculate stats
  const stats = useMemo(() => {
    let wins = 0;
    let losses = 0;
    let ties = 0;
    let gamesPlayed = 0;

    for (const game of teamGames) {
      const status = getGameStatus(game);
      if (status !== 'finished') continue;

      gamesPlayed++;
      if (game.is_tie) {
        ties++;
      } else if (game.winner_id === teamIdNum) {
        wins++;
      } else {
        losses++;
      }
    }

    return { wins, losses, ties, gamesPlayed };
  }, [teamGames, teamIdNum]);

  const isLoading = isLoadingTeam || isLoadingGames;

  if (!teamIdNum) {
    return (
      <Box>
        <Alert severity="error">Invalid team ID</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/admin/teams')} sx={{ mt: 2 }}>
          Back to Teams
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link component={RouterLink} to="/admin" underline="hover" color="inherit">
          Admin
        </Link>
        <Link component={RouterLink} to="/admin/teams" underline="hover" color="inherit">
          Teams
        </Link>
        <Typography color="text.primary">{team?.team_name || `Team ${teamId}`}</Typography>
      </Breadcrumbs>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : teamError ? (
        <Box>
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load team details
          </Alert>
          <Button startIcon={<BackIcon />} onClick={() => navigate('/admin/teams')}>
            Back to Teams
          </Button>
        </Box>
      ) : team ? (
        <>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Typography variant="h4" fontWeight={700}>
              {team.team_name}
            </Typography>
            {team.is_disqualified ? (
              <Button
                variant="outlined"
                color="success"
                onClick={handleUndisqualifyClick}
              >
                Undo Disqualify
              </Button>
            ) : (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DisqualifyIcon />}
                onClick={handleDisqualifyClick}
              >
                Disqualify
              </Button>
            )}
          </Box>

          {/* Error alerts */}
          {disqualifyMutation.isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Failed to disqualify team: {(disqualifyMutation.error as Error).message}
            </Alert>
          )}
          {undisqualifyMutation.isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Failed to undisqualify team: {(undisqualifyMutation.error as Error).message}
            </Alert>
          )}

          {/* Tags */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
            <Chip
              label={team.league_id.toUpperCase()}
              color={
                team.league_id === 'yle' ? 'primary' : team.league_id === 'pro' ? 'secondary' : 'default'
              }
            />
            {team.variant && <Chip label={team.variant} variant="outlined" />}
            {team.is_disqualified && (
              <Chip label={`Disqualified (Round ${team.disqualify_round})`} color="error" />
            )}
          </Box>

          {/* Two column layout for info */}
          <Box
            sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 3 }}
          >
            {/* Contact Info */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Contact Information
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {team.contact_name && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon fontSize="small" color="action" />
                    <Typography variant="body2">{team.contact_name}</Typography>
                  </Box>
                )}
                {team.contact_phone && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PhoneIcon fontSize="small" color="action" />
                    <Typography variant="body2" sx={{ minWidth: 140 }}>
                      {team.contact_phone}
                    </Typography>
                    <Tooltip title={copied ? 'Copied!' : 'Copy phone'}>
                      <IconButton size="small" onClick={() => handleCopyPhone(team.contact_phone)}>
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
                {team.contact_email && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmailIcon fontSize="small" color="action" />
                    <Typography variant="body2">{team.contact_email}</Typography>
                  </Box>
                )}
                {team.store_id && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ReceiptIcon fontSize="small" color="action" />
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {team.store_id}
                    </Typography>
                    <Tooltip title={copied ? 'Copied!' : 'Copy order ID'}>
                      <IconButton size="small" onClick={() => handleCopyPhone(team.store_id)}>
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
                {!team.contact_name && !team.contact_phone && !team.contact_email && !team.store_id && (
                  <Typography variant="body2" color="text.secondary">
                    No contact information available
                  </Typography>
                )}
              </Box>
            </Paper>

            {/* Stats */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Record
              </Typography>
              <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight={700} color="success.main">
                    {stats.wins}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Wins
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight={700} color="warning.main">
                    {stats.ties}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Ties
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight={700} color="error.main">
                    {stats.losses}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Losses
                  </Typography>
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight={700}>
                    {stats.gamesPlayed}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Played
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Box>

          {/* Comments Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Comments
            </Typography>

            {/* Add Comment */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                size="small"
                placeholder="Add a comment..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleAddComment();
                  }
                }}
                sx={{ flexGrow: 1 }}
              />
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddComment}
                disabled={!newComment.trim() || createCommentMutation.isPending}
              >
                Add
              </Button>
            </Box>

            {/* Comments List */}
            {comments.length > 0 && (
              <Paper variant="outlined">
                <List dense disablePadding>
                  {comments.map((comment, index) => (
                    <ListItem
                      key={comment.id}
                      divider={index < comments.length - 1}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => deleteCommentMutation.mutate(comment.id)}
                          disabled={deleteCommentMutation.isPending}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      }
                    >
                      <ListItemText
                        primary={comment.comment}
                        secondary={`${comment.created_by_username ?? 'Unknown'} - ${new Date(comment.created_at).toLocaleString('fi-FI')}`}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}
          </Box>

          {/* Games List */}
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Games ({teamGames.length})
          </Typography>

          {teamGames.length > 0 ? (
            <Paper sx={{ height: 400, overflow: 'hidden' }}>
              <GamesListTable
                games={teamGames}
                teamPerspective={teamIdNum}
                columns={{ time: true, block: true, court: true, teams: true, score: true, status: true }}
                onGameClick={handleGameClick}
                onBlockClick={handleBlockClick}
                onCourtClick={handleCourtClick}
                onTeamClick={handleTeamClick}
                variant="elevation"
                maxHeight={400}
              />
            </Paper>
          ) : (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">No games scheduled</Typography>
            </Paper>
          )}

          {/* Event Log */}
          <EventLog params={{ team_id: teamIdNum }} title="Team Event Log" />

          {/* Disqualify Confirmation Dialog */}
          <Dialog
            open={disqualifyDialogOpen}
            onClose={() => setDisqualifyDialogOpen(false)}
          >
            <DialogTitle>Disqualify Team</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Are you sure you want to disqualify <strong>{team.team_name}</strong>?
                The team will be marked as disqualified and removed from the ongoing tournament.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDisqualifyDialogOpen(false)}>Cancel</Button>
              <Button
                color="error"
                variant="contained"
                onClick={handleDisqualifyConfirm}
                disabled={disqualifyMutation.isPending}
              >
                {disqualifyMutation.isPending ? 'Disqualifying...' : 'Disqualify'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Undisqualify Confirmation Dialog */}
          <Dialog
            open={undisqualifyDialogOpen}
            onClose={() => setUndisqualifyDialogOpen(false)}
          >
            <DialogTitle>Undisqualify Team</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Are you sure you want to undisqualify <strong>{team.team_name}</strong>?
                <br /><br />
                This will restore cancelled games and undo winner propagation for affected blocks.
                Teams that advanced due to this disqualification may be removed from their next-round games.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setUndisqualifyDialogOpen(false)}>Cancel</Button>
              <Button
                color="success"
                variant="contained"
                onClick={handleUndisqualifyConfirm}
                disabled={undisqualifyMutation.isPending}
              >
                {undisqualifyMutation.isPending ? 'Processing...' : 'Undisqualify'}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      ) : null}
    </Box>
  );
}
